import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectionStrategy,
  signal,
  input,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { ReplaySubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, pairwise, startWith, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import { BrowserService } from 'app/modules/social/services/browser.service';
import { FavoriteService } from 'app/modules/social/services/favorite.service';
import User from 'entity/User';
import { BaseUser } from 'messages/websocket.messages';

import { GalleryService, CustomGalleryTabs, groupParticipants } from '../../../services/gallery/gallery.service';
import { SceneService } from '../../../services/scene.service';
import { ShareScreenParticipantService } from '../../../services/share-screen/share-screen-participant.service';
import { ShareScreenService } from '../../../services/share-screen/share-screen.service';
import { selectors, socialAppActions, socialAppSelectors } from '../../../state/app';

import { GalleryAnimation } from './animation';
import { GalleryTabs, GalleryTabsTypes } from './constants';

@Component({
  selector: 'presentation-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  animations: [GalleryAnimation],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationGalleryComponent implements OnDestroy, OnInit, AfterViewInit {
  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger?: MatAutocompleteTrigger;
  @ViewChild('inputFilterOptions') private inputFilterOptions?: ElementRef<HTMLInputElement>;
  public readonly isGalleryOpen = input(true);
  @Output() public galleryChange = new EventEmitter<boolean>();

  public gallery: User[] = [];
  public galleryGroups: groupParticipants[] = [];
  public galleryTabs = signal<GalleryTabs[]>([
    { icon: 'dashboard', name: 'Gallery', isSelected: true, type: GalleryTabsTypes.ALL, isDeleteable: false },
    { icon: 'star', name: 'Favorites', isSelected: false, type: GalleryTabsTypes.FAVORITES, isDeleteable: false },
  ]);
  public customGalleryTabs: GalleryTabs[] = [];
  public prevGallery: User[] = [];
  public newVisualIndicators = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected isCrossRoom = false;
  protected customTabs?: CustomGalleryTabs[];
  protected customTabSearch = '';
  protected addingFilter = false;
  public control = new FormControl('');
  private speakingParticipants: User[] = [];
  private activeSpeakersUsers: string[] = [];
  private isOpenModeActive = false;
  private baseUsersList: BaseUser[] = [];

  public secondRow: User[] = [];
  private lastGalleryTab: number | undefined;

  protected presentationV3 = signal(false);
  protected isMobile = false;

  private smartBadgeUpdated = false;

  public constructor(
    public shareScreenService: ShareScreenService,
    public shareScreenParticipantService: ShareScreenParticipantService,
    private sceneService: SceneService,
    protected galleryService: GalleryService,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private favoriteService: FavoriteService,
    private browserService: BrowserService,
  ) {
    this.sceneService.sceneManager?.participantAdded$
      .pipe(
        distinctUntilChanged((a, b) => _.isEqual(a, b)),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.filterSelectedGallery();
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(selectors.getGallery)
      .pipe(startWith([]), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prev, curr]) => {
        // when a gallery changes and tabs are selected filters the gallery
        this.filterSelectedGallery(curr);
        this.gallery = curr;
        this.prevGallery = prev;
        this.changeDetectorRef.markForCheck();
      });

    // Event state
    this.store
      .select(selectors.isEventActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        // Reset gallery if presentation starts and group share was in progress
        if (active && (this.galleryService.groupSharingUserId || this.galleryService.isUserSharingToGroup)) {
          this.galleryService.reset();
          this.changeDetectorRef.markForCheck();
        }
      });

    this.store
      .select(selectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => {
        this.isCrossRoom = isCrossRoom;
        this.changeDetectorRef.markForCheck();
      });

    this.favoriteService.favoriteListStream$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      // filters the gallery if the favorite list changes
      if (this.tabSelectedType === GalleryTabsTypes.FAVORITES) {
        this.filterSelectedGallery();
        this.changeDetectorRef.markForCheck();
      }
    });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => {
        this.newVisualIndicators = newVisualIndicators;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => this.presentationV3.set(presentation_v3));

    combineLatest([
      this.store.select(selectors.getActiveSpeakers),
      this.store.select(selectors.getSpeakingParticipantValues),
      this.store.select(selectors.isOpenModeActive),
      this.store.select(selectors.isSinglePushToTalkActive),
    ])
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(([activeSpeakers, speakingParticipants, isOpenModeActive, isSinglePushToTalkActive]) => {
        this.isOpenModeActive = isOpenModeActive;

        if (isOpenModeActive) {
          this.activeSpeakersUsers = activeSpeakers;
          this.changeDetectorRef.markForCheck();
          return;
        }

        if (isSinglePushToTalkActive) {
          this.speakingParticipants = speakingParticipants.length ? [speakingParticipants[0]] : [];
          this.changeDetectorRef.markForCheck();
          return;
        }

        this.speakingParticipants = speakingParticipants;
        this.changeDetectorRef.markForCheck();
      });

    this.galleryService.allBaseUsers$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((baseUsersList) => {
        this.baseUsersList = baseUsersList;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(selectors.getGalleryTabs)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((galleryTabs) => {
        if (galleryTabs.length > 0) {
          const storageTabs = JSON.stringify(galleryTabs);
          this.galleryTabs.update((t) => [...t, ...JSON.parse(storageTabs)]);
          this.customGalleryTabs = JSON.parse(storageTabs);
          this.changeDetectorRef.markForCheck();
        }
      });

    this.store
      .select(socialAppSelectors.isUserEditBadge)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => {
        if (userId === '') {
          this.smartBadgeUpdated = true;
          this.filterSelectedGallery(this.gallery);
          this.changeDetectorRef.markForCheck();
        }
      });

    // Subscribe to the form control value changes to update the customTabSearch
    this.control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.customTabSearch = value || '';
      this.changeDetectorRef.markForCheck();
    });
  }

  public async ngOnInit() {
    this.isMobile = this.browserService.isMobile();
    this.galleryService.init();
    await this.galleryService.getGalleryTabs();
    this.customTabs = this.galleryService.customGalleryTabs;
    this.filterSelectedGallery();
    this.changeDetectorRef.markForCheck();
  }

  public ngAfterViewInit(): void {
    this.galleryService.setGallerySize();
    this.changeDetectorRef.markForCheck();
  }

  public ngOnDestroy(): void {
    if (this.galleryService.hasBadgingQuestions) {
      this.store.dispatch(socialAppActions.UPDATE_GALLERY_TABS({ galleryTabs: this.customGalleryTabs }));
    }
    this.sceneService.sceneManager?.player?.videoElement?.pause();
    this.galleryService.destroy();
    this.gallery = [];
    this.galleryGroups = [];
    this.customTabs = [];
    this.inputFilterOptions = undefined;
    this.autocompleteTrigger = undefined;

    if (document.pictureInPictureEnabled) {
      document.exitPictureInPicture().catch((err) => new Error(err));
    }

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public checkGalleryState(user: User): string {
    const ids = this.gallery.map((u) => u.userId);
    const prevIds = this.prevGallery.map((u) => u.userId);
    let state = 'noAnimation';
    if (!ids.includes(user.userId) && prevIds.includes(user.userId)) {
      state = 'fade-out';
    }

    if (ids.includes(user.userId) && !prevIds.includes(user.userId)) {
      state = 'fade-in';
    }
    return state;
  }

  public nextPage(): void {
    this.galleryService.nextPage();
  }

  public previousPage(): void {
    this.galleryService.previousPage();
  }

  // Styling for Gallery and Participants in Gallery
  public getParticipantStyles() {
    return {
      height: this.videoSize + 'px',
      width: this.videoSize + 'px',
      margin: '4px 8px',
    };
  }

  public getContainerStyles() {
    const MIN_MARGIN = this.galleryService.minContainerMarginTop;
    const rows = this.galleryService.singleRowLayout ? 1 : 2;
    const width = (this.galleryService.gallerySize / rows) * (this.videoSize + this.galleryService.participantMargin);
    const marginTop = this.galleryService.pageCount === 1 || !this.showPagination ? 50 : MIN_MARGIN;

    if (this.presentationV3()) {
      if (this.isMobile) {
        if (this.isGalleryOpen()) {
          return {
            'max-width': width + 'px',
            'margin-top': this.shouldShowLabel ? '8px' : '45px',
          };
        } else {
          return {
            'max-width': width + 'px',
            'margin-top': '70px',
          };
        }
      }

      return {
        'max-width': width + 'px',
        'margin-top': this.shouldShowLabel ? '8px' : '32px',
      };
    }
    return {
      'max-width': width + 'px',
      'margin-top': marginTop + 'px',
    };
  }

  public async openFilter(): Promise<void> {
    this.customTabSearch = '';
    this.control.setValue('');
    this.addingFilter = true;
    this.customTabs = this.galleryService.customGalleryTabs;
    await delayFor(150);
    this.inputFilterOptions?.nativeElement.focus();
  }

  public createTab(option: CustomGalleryTabs): void {
    this.customTabSearch = '';
    const tabCreated: GalleryTabs = {
      name: option.title,
      isSelected: false,
      type: option.type as GalleryTabsTypes,
      question: option.question,
      isDeleteable: true,
    };

    this.galleryTabs.update((t) => [...t, tabCreated]);
    this.customGalleryTabs.push(tabCreated);

    this.changeTab(this.galleryTabs().length - 1);
    this.control.setValue('');
  }

  public changeTab(tabIndex: number): void {
    if (this.presentationV3() && !this.isGalleryOpen()) {
      this.toggleGallery();
    }
    if (this.selectedGalleryTab === tabIndex) {
      return;
    }
    this.galleryTabs.update((t) => {
      t.forEach((tab, index) => {
        tab.isSelected = index === tabIndex;
      });
      return t;
    });
    this.secondRow = [];

    this.filterSelectedGallery();
  }

  private filterSelectedGallery(gallery?: User[]): void {
    const selectedTabGallery = this.galleryTabs()[this.selectedGalleryTab];
    const galleryGroups = gallery
      ? this.galleryService.filterGallery(selectedTabGallery, gallery)
      : this.galleryService.filterGallery(selectedTabGallery) || [];
    // only trigger a repaint if necessary
    if (
      galleryGroups
      // TODO: is this comparison nessasary?
      // && !_.isEqual(this.filterIgnoredProps(this.galleryGroups), this.filterIgnoredProps(galleryGroups))
    ) {
      // mutate this.galleryGroups instead of replacing it to avoid unnecessary repaints
      if (this.selectedGalleryTab !== this.lastGalleryTab || this.smartBadgeUpdated) {
        this.lastGalleryTab = this.selectedGalleryTab;
        // use cloneDeep because state is immutable, spread operator does not work after the first level
        this.galleryGroups = _.cloneDeep(galleryGroups);
        this.smartBadgeUpdated = false;
      } else {
        // add to this.galleryGroups if absent
        galleryGroups.forEach((group, gI) => {
          // is comparing by userId too shallow?
          const toAdd = _.differenceBy(group.participants, this.galleryGroups[gI]?.participants, 'userId');
          // const toAdd = _.difference(group.participants, this.galleryGroups[gI]?.participants);
          if (!this.galleryGroups[gI]) {
            this.galleryGroups[gI] = { groupName: group.groupName, participants: [] };
          }
          this.galleryGroups[gI].participants.push(...toAdd);
          // remove from this.galleryGroups if absent
          const toRemove = _.differenceBy(this.galleryGroups[gI]?.participants, group.participants, 'userId');
          toRemove.forEach((participant) => {
            this.galleryGroups[gI].participants.splice(this.galleryGroups[gI].participants.indexOf(participant), 1);
          });
        });
      }
    }
  }

  private filterIgnoredProps(groups: groupParticipants[]): groupParticipants[] {
    return groups.map((group) => {
      return {
        ...group,
        participants: group.participants
          .map((user) => {
            return { ...user, cameraOn: false, microphoneOn: false };
          })
          .sort((a, b) => {
            const aId = a.userId || '';
            const bId = b.userId || '';
            return aId > bId ? 1 : -1;
          }),
      };
    });
  }

  public removeTab(tabIndex: number) {
    if (tabIndex === this.selectedGalleryTab) {
      this.changeTab(this.selectedGalleryTab - 1);
    }

    const tabTitle = this.galleryTabs()[tabIndex].name;
    this.customGalleryTabs.splice(
      this.customGalleryTabs.findIndex((tab) => tab.name === tabTitle),
      1,
    );

    this.galleryTabs.update((t) => {
      t.splice(tabIndex, 1);
      return t;
    });
  }

  public get videoSize(): number {
    return this.galleryService.videoSize;
  }

  public get participantSize(): string {
    return this.galleryService.videoSize + 'px';
  }

  public get selectedGalleryTab(): number {
    return this.galleryTabs().findIndex((tab) => tab.isSelected === true);
  }

  public get tabSelectedType(): GalleryTabsTypes | undefined {
    return this.galleryTabs().find((tab) => tab.isSelected === true)?.type;
  }

  protected getWrapperStyle(participants: User[]) {
    const style: Record<string, string> = { alignItems: 'center' };
    if (participants.length > 1) {
      style.alignItems = 'flex-start';
    }
    if (this.shouldShowLabel) {
      style.border = '2px solid #405b95';
    }
    return style;
  }

  public getParticipantRingColor(userId: string | undefined): string | undefined {
    if (!userId) {
      return;
    }
    // if is a cross room it will get the ring color from a baseUser
    const baseUser = this.baseUsersList.find((user) => user.userId === userId);

    if (baseUser && baseUser.ringColor) {
      return baseUser.ringColor;
    }

    const ringColor = this.galleryService.getRingColor(userId);

    return ringColor;
  }

  public isUserSpeaking(user: User): boolean {
    if (this.isOpenModeActive) {
      return this.activeSpeakersUsers.includes(user.userId as string);
    } else {
      return this.speakingParticipants.includes(user);
    }
  }

  public get isFavoritesEmpty() {
    const favoritesSelected = this.galleryTabs()[this.selectedGalleryTab].type === GalleryTabsTypes.FAVORITES;
    if (favoritesSelected && this.galleryGroups && !this.galleryGroups[0].participants.length) {
      return true;
    }
    return false;
  }

  public get isGalleryEmpty() {
    const participants = this.galleryGroups?.flatMap((group) => group.participants).length;
    return !!participants;
  }

  public get shouldShowLabel() {
    if (!this.tabSelectedType) {
      return false;
    }
    const showLabel = [GalleryTabsTypes.BADGE_ANSWER, GalleryTabsTypes.BADGE_QUESTION];
    return showLabel.includes(this.tabSelectedType);
  }

  public get filteredTabs() {
    if (typeof this.customTabSearch !== 'string' || this.customTabSearch === '') {
      return this.customTabs;
    }
    const filterOption = this.customTabSearch.toLowerCase();
    return this.customTabs?.filter((customTab) => customTab.title.toLowerCase().includes(filterOption));
  }

  protected get getParticipantGroupStyle() {
    return {
      'flex-wrap': this.galleryService.singleRowLayout ? 'nowrap' : 'wrap',
    };
  }

  protected get showPagination() {
    const flatGalleryGroups = this.galleryGroups?.flatMap((group) => group.participants);
    if (this.tabSelectedType === GalleryTabsTypes.ALL) {
      return true;
    }
    return flatGalleryGroups && flatGalleryGroups.length >= this.gallery.length;
  }

  public filterOptions(option: CustomGalleryTabs): boolean {
    return !this.customGalleryTabs.some((tab) => tab.name === option.title);
  }

  protected toggleGallery() {
    this.galleryChange.emit(!this.isGalleryOpen());
  }

  protected getTruncateSize(groupLength: number) {
    if (groupLength === 1) {
      return 6;
    }

    if (groupLength >= 3) {
      return 14;
    }

    if (groupLength >= 8) {
      return 24;
    }
    return 12;
  }

  protected async closeInput() {
    // await for animation end
    await delayFor(200);
    this.addingFilter = false;
    this.inputFilterOptions?.nativeElement.blur();
    this.autocompleteTrigger?.closePanel();
  }
}
